import { useState } from 'react';
import { Select } from '@/components/Select';
import type { FirebaseAccount, FirebaseOrg, FirebaseChoice } from '@/api/apps-setup';

// What a new-project run is doing now, keyed by the last step it finished.
// The same labels serve both routes: an organisation run (API) and a browser
// profile run (the runner in the account's browser, which adds 'key').
export const PROVISION_STEP_LABEL: Record<string, string> = {
  new: 'creating the project',
  project: 'adding Firebase / getting the service account key',
  key: 'giving the key access to Google Analytics',
  firebase: 'linking Google Analytics',
  analytics: 'enabling APIs',
  services: 'setting up the service account',
  sa: 'registering the app',
};

// Firebase choice for an import: an existing project, or a brand-new project
// the platform creates in one of the organisations. The two lists live behind
// a toggle instead of one long dropdown, so "new project" is an explicit
// decision rather than an entry buried among fifty bundle ids.
interface Props {
  accounts: FirebaseAccount[];
  orgs: FirebaseOrg[];
  value: FirebaseChoice | '';
  onChange: (v: FirebaseChoice | '') => void;
  compact?: boolean; // history card: native select, smaller controls
}

export function FirebasePicker({ accounts, orgs, value, onChange, compact }: Props) {
  const [mode, setMode] = useState<'existing' | 'new'>(value.startsWith('org:') || value.startsWith('bp:') ? 'new' : 'existing');
  const canCreate = orgs.length > 0;

  const switchTo = (m: 'existing' | 'new') => {
    setMode(m);
    onChange(''); // the old pick belongs to the other list
  };

  // Each organisation shows how many of its slots are taken; a full one is
  // hidden so nobody starts a run the server would refuse — unless it is the
  // current pick, which stays visible rather than silently vanishing.
  const options = mode === 'new'
    ? orgs
      .filter((o) => o.projects < o.max_projects || value === `${o.kind ?? 'org'}:${o.id}`)
      .map((o) => ({ value: `${o.kind ?? 'org'}:${o.id}`, label: `${o.name} · ${o.projects}/${o.max_projects}` }))
    : accounts.map((f) => ({ value: `acc:${f.id}`, label: f.name }));
  const placeholder = mode === 'new' ? 'Where to create it' : (compact ? 'Select Firebase project' : 'No Firebase');

  const toggle = canCreate && (
    <button
      type="button"
      onClick={() => switchTo(mode === 'new' ? 'existing' : 'new')}
      className="text-xs text-info hover:underline whitespace-nowrap"
    >
      {mode === 'new' ? 'Use existing project' : '+ New Firebase project'}
    </button>
  );

  if (compact) {
    return (
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <select
          value={value}
          onChange={(e) => onChange((e.target.value as FirebaseChoice) || '')}
          className="w-full min-w-0 px-2 py-1 text-xs rounded-md border border-border bg-surface-muted text-content"
        >
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {toggle}
      </div>
    );
  }

  // A div, not a <label>: the toggle button would become the label's control
  // and a browser could hand it clicks made on the dropdown's options.
  return (
    <div className="flex flex-col gap-1 min-w-[12rem] flex-1">
      <span className="flex items-center justify-between gap-2">
        <span className="text-content-subtle text-xs">{mode === 'new' ? 'New Firebase project in' : 'Firebase'}</span>
        {toggle}
      </span>
      <Select
        value={value}
        onChange={(v) => onChange((v as FirebaseChoice) || '')}
        options={[{ value: '', label: placeholder }, ...options]}
        className="w-full"
      />
    </div>
  );
}
